import { KnownDeliveryDtoDeliveryType, OpenLockerResponseDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { RestError } from '@azure/core-http';
import { useMemo, useState } from 'react';
import {
  useMutationOpenLocker,
  useQueryCanIOpenLocker,
  useQueryGetLocker,
  useQueryListLockerDoors
} from '../../../lockers.queries';
import { useQueryListDeliveryContainerContents } from '../../../hubs.queries';
import { useQueryGetHub } from '../../../hubs.queries';
import {
  useMutationMarkOrderCancelled,
  useMutationMarkOrderCollected,
  useMutationMarkOrderRetrieved
} from '../../../../orders/details/order-details.queries';
import { ValidOrderStatusChangeOption } from '../../../../orders/details/actions/open-locker/open-locker-action.hook';
import {
  useMutationMarkDeliveryCancel,
  useMutationMarkDeliveryCollected,
  useMutationMarkDeliveryRetrieved
} from '../../../../deliveries/details/deliveries-details.queries';

export interface OpenLockerActionProps {
  lockerID: string;
  projectID: string;
  onAbort(): void;
  onSuccess(statusHasChanged: boolean): void;
}

export interface OpenLockerActionState {
  error?: RestError;
  loading: boolean;
  selectedDoorID: string;
}

interface IntentToOpen extends OpenLockerActionState {
  current: 'OPEN_REQUEST';
}

export interface OpenResultSuccess extends OpenLockerActionState {
  current: 'OPEN_REQUEST_SUCCESS';
  newStatus?: ValidOrderStatusChangeOption;
}

export interface OpenResultFailure extends OpenLockerActionState {
  current: 'OPEN_REQUEST_FAILURE';
  openLockerResponse?: OpenLockerResponseDto;
}

export type OpenLockerActionStates = IntentToOpen | OpenResultSuccess | OpenResultFailure;

export interface OpenLockerActionHandlers {
  onCancelIntent(): void;
  onSendRequest(): void;
  onStatusInput(status: ValidOrderStatusChangeOption): void;
  onStatusChange(): void;
  onDone(): void;
  onContinueToStatusChange(): void;
}

const initialState: OpenLockerActionStates = {
  current: 'OPEN_REQUEST',
  loading: false,
  selectedDoorID: ''
};

export function useOpenLockerAction({ lockerID, onAbort, onSuccess }: Omit<OpenLockerActionProps, 'hubName'>) {
  const [state, setState] = useState<OpenLockerActionStates>(initialState);
  const openLockerAction = useMutationOpenLocker();
  const locker = useQueryGetLocker(lockerID);
  const doors = useQueryListLockerDoors(lockerID);
  const hub = useQueryGetHub(locker.data?.hub.id);
  const lockerContents = useQueryListDeliveryContainerContents(lockerID);

  // Even if the User has permission to open this locker, they still might not be able to.
  // For example, the locker might contain deliveries for another project.
  const canOpenLocker = useQueryCanIOpenLocker(lockerID);

  // Everything except the locker contents need to be loaded before we display the action modal.
  const isLoading = doors.isLoading || hub.isLoading || canOpenLocker.isLoading;
  const isError = doors.error || hub.error || canOpenLocker.error || lockerContents.error;

  const markOrderCancelledMutation = useMutationMarkOrderCancelled();
  const markOrderCollectedMutation = useMutationMarkOrderCollected();
  const markOrderRetrievedMutation = useMutationMarkOrderRetrieved();

  const markDeliveryCancelledMutation = useMutationMarkDeliveryCancel();
  const markDeliveryCollectedMutation = useMutationMarkDeliveryCollected();
  const markDeliveryRetrievedMutation = useMutationMarkDeliveryRetrieved();

  // Prepare the Door select options
  const doorOptions = useMemo(() => {
    if (doors.isLoading) {
      return [];
    }
    // We only show door select options if there are multiple doors
    if (!doors.data || doors.data.length < 2) {
      return [];
    }
    return doors.data.map((door) => ({
      label: door.positionDescription ?? `Door with hinge on ${door.hingeSide.toLowerCase()} side.`,
      value: door.id
    }));
  }, [doors.data, doors.isLoading]);

  const multipleDoorsProps = useMemo(() => {
    if (doorOptions.length < 2) {
      return undefined;
    }
    return {
      doorOptions,
      onSelectDoor(doorID: string) {
        setState((state) => ({ ...state, selectedDoorID: doorID }));
      }
    };
  }, [doorOptions]);

  const handlers: OpenLockerActionHandlers = {
    onCancelIntent() {
      onAbort();
    },
    onSendRequest() {
      setState((state) => ({ ...state, loading: true }));
      openLockerAction.mutate(
        { lockerID: lockerID, doorIDs: state.selectedDoorID ? [state.selectedDoorID] : undefined },
        {
          onSuccess(response) {
            if (response.status === 'success') {
              setState((state) => ({ ...state, current: 'OPEN_REQUEST_SUCCESS', loading: false }));
            }
            if (response.status === 'failed') {
              setState((state) => ({
                ...state,
                current: 'OPEN_REQUEST_FAILURE',
                loading: false,
                openLockerResponse: response
              }));
            }
          },
          onError(error) {
            setState((state) => ({ ...state, current: 'OPEN_REQUEST_FAILURE', loading: false, error: error }));
          }
        }
      );
    },
    onStatusInput(newStatus) {
      switch (state.current) {
        case 'OPEN_REQUEST_SUCCESS':
          setState((state) => ({ ...state, newStatus }));
          break;
        default:
          return;
      }
    },
    onStatusChange() {
      if (state.current === 'OPEN_REQUEST_SUCCESS') {
        if (lockerContents.data?.[0] === undefined) {
          return;
        }

        const mutationOptions = {
          onSuccess() {
            onSuccess(true);
          },
          onError(error: RestError) {
            setState((state) => ({ ...state, current: 'OPEN_REQUEST_SUCCESS', error: error, loading: false }));
          }
        };

        // Retail flow
        if (lockerContents.data?.[0]?.delivery?.deliveryType === KnownDeliveryDtoDeliveryType.RetailOrder) {
          const projectID = lockerContents.data?.[0]?.delivery?.project.id;
          const orderID = lockerContents.data?.[0]?.delivery?.workflow?.id;

          if (!orderID || !projectID) {
            return;
          }

          if (state.newStatus === 'cancelled') {
            markOrderCancelledMutation.mutate({ retailerID: projectID, orderID }, mutationOptions);
          }
          if (state.newStatus === 'collected') {
            markOrderCollectedMutation.mutate({ retailerID: projectID, orderID }, mutationOptions);
          }
          if (state.newStatus === 'retrieved') {
            markOrderRetrievedMutation.mutate({ retailerID: projectID, orderID }, mutationOptions);
          }

          return;
        }

        // Non-retail flow
        const deliveryID = lockerContents.data?.[0]?.delivery?.id;

        if (!deliveryID) {
          return;
        }

        if (state.newStatus === 'cancelled') {
          markDeliveryCancelledMutation.mutate({ deliveryID }, mutationOptions);
        }
        if (state.newStatus === 'collected') {
          markDeliveryCollectedMutation.mutate({ deliveryID }, mutationOptions);
        }
        if (state.newStatus === 'retrieved') {
          markDeliveryRetrievedMutation.mutate({ deliveryID }, mutationOptions);
        }
      }
    },
    onDone() {
      if (state.current === 'OPEN_REQUEST_SUCCESS') {
        onSuccess(false);
      }
    },
    onContinueToStatusChange() {
      setState((state) => ({ ...state, current: 'OPEN_REQUEST_SUCCESS', loading: false }));
    }
  };

  return {
    state,
    handlers,
    hub: hub.data,
    lockerContents: lockerContents.data,
    multipleDoors: multipleDoorsProps,
    canOpenLocker: canOpenLocker.data,
    isLoading,
    isError
  } as const;
}
